import { useState } from "react";
import type { CardDefinition, CaseDefinition, NodeDefinition } from "../../domain/types/index.js";
import { getStageRewardPools } from "../../content/stages.js";
import { getCard } from "../../content/lookups.js";
import { nodeTitle, sizeLabel } from "../shared/labels.js";
import type { NodeVisualState } from "./mapLayout.js";
import { NodeRewardSection } from "./NodeRewardSection.js";
import { nodeAlt, nodeIcon } from "./mapNodeIcon.js";
import { AttackProfile } from "../shared/AttackProfile.js";

type Props = {
  node: NodeDefinition;
  spec: CaseDefinition | null;
  visual: NodeVisualState;
  stageId?: string;
  canProceed?: boolean;
  onClose: () => void;
  onProceed: () => void;
};

export function NodeDetailSheet({ node, spec, visual, stageId, canProceed = true, onClose, onProceed }: Props) {
  const [isRewardPoolOpen, setIsRewardPoolOpen] = useState(false);
  const title = node.kind === "start" ? "開始地点" : node.kind === "rest" ? "休日" : node.kind === "event" ? "はてなマス" : node.kind === "relic" ? "基盤マス" : nodeTitle(node.id, spec?.name);
  const badge = spec ? `${sizeLabel(spec.size)}規模` : node.kind === "rest" ? "休日" : node.kind === "event" ? "イベント" : node.kind === "relic" ? "基盤" : "開始";
  const rewardPool = spec && !spec.isBoss && (spec.size === "small" || spec.size === "medium" || spec.size === "large")
    ? getStageRewardPools(stageId)[spec.size]
    : null;
  const rewardCards = rewardPool
    ? [...rewardPool.workers, ...rewardPool.actions]
      .map((id) => getCard(id))
      .filter((card): card is CardDefinition => Boolean(card))
    : [];
  return (
    <div className="map-sheet-backdrop" onClick={onClose}>
      <section
        className="map-sheet"
        role="dialog"
        aria-label={`${title}の詳細`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="map-sheet-body">
          <header className="map-sheet-head">
            <img className="map-sheet-icon" src={nodeIcon(node, spec)} alt={nodeAlt(node)} />
            <div>
              <small>{badge}</small>
              <h2>{title}</h2>
            </div>
          </header>
          {spec && (
            <p className="map-sheet-stats">
              <span>
                <img src="/icon-progress.png" alt="進捗" />
                進捗 {node.required ?? spec.required}
              </span>
              <span>
                <img src="/icon-deadline.png" alt="工期" />
                工期 {node.deadline ?? spec.deadline}
              </span>
            </p>
          )}
          <p className="map-sheet-flavor">
            {spec?.flavor ?? (node.kind === "rest" ? "休日を取り、疲労を回復する。" : node.kind === "event" ? "何が起こるか分からない。" : node.kind === "relic" ? "組織基盤を1つ取得する。カード報酬はない。" : "ここから仕事の一日が始まる。")}
          </p>
          {spec && <AttackProfile key={spec.id} spec={spec} />}
          {rewardPool && spec && (
            <NodeRewardSection
              spec={spec}
              rewardCards={rewardCards}
              isOpen={isRewardPoolOpen}
              stageId={stageId}
              onToggle={() => setIsRewardPoolOpen((isOpen) => !isOpen)}
            />
          )}
        </div>
        <div className="map-sheet-actions">
          <button type="button" className="btn" onClick={onClose}>閉じる</button>
          {canProceed && visual === "open" && (
            <button type="button" className="btn btn-primary" onClick={onProceed}>進む</button>
          )}
        </div>
      </section>
    </div>
  );
}
